import React, { useEffect, useState, useRef } from 'react';
import { Plus, PenLine, ShieldAlert, Sparkles } from 'lucide-react';
import { api, DEMO } from '../lib/api';
import { hasCap } from '../lib/capabilities';
import AiNoteGeneratorModal from './AiNoteGeneratorModal';

const GROUP_NOTE_STATUS_LABELS = { draft: 'Draft', ready_for_signature: 'Ready to sign', signed: 'Signed', voided: 'Voided' };
const GROUP_NOTE_STATUS_BADGE = { draft: 'badge-warn', ready_for_signature: 'badge-warn', signed: 'badge-ok', voided: 'badge-neutral' };
const GROUP_ATTENDANCE_LABELS = { present: 'Present', late: 'Late', left_early: 'Left early', absent: 'Absent', no_show: 'No-show', excused: 'Excused' };
const GROUP_ATTENDANCE_BADGE = { present: 'badge-ok', late: 'badge-ok', left_early: 'badge-warn', absent: 'badge-neutral', no_show: 'badge-danger', excused: 'badge-neutral' };
const PARTICIPATION_LEVELS = ['active', 'moderate', 'limited', 'minimal', 'declined', 'disruptive', 'other'];
const PROGRESS_RATINGS = ['improving', 'unchanged', 'worsening', 'unable_to_assess'];

function emptyForm(defaultEnrollmentId = '', defaultSessionId = '') {
  return {
    id: null,
    addendum_of: null,
    original_version: 1,
    group_session_id: defaultSessionId,
    enrollment_id: defaultEnrollmentId,
    attendance_status: 'present',
    participation_level: 'active',
    progress_rating: 'improving',
    status: 'draft',
    assessment: '',
    presentation: '',
    intervention: '',
    response_to_intervention: '',
    individualized_response: '',
    response: '',
    evaluation: '',
    progress: '',
    relevant_verbalization: '',
    attitude: '',
    concentration: '',
    follow_directions: '',
    peer_interaction: '',
    clinical_barriers: '',
    continued_need: '',
    si_status: '',
    hi_status: '',
    substance_concern: '',
    safety_concern: false,
    safety_note: '',
    plan: '',
  };
}

export default function PhpGroupResponses({ patient, user, notify, navigate, openAssessments, programType = 'php' }) {
  const canManage = hasCap(user, 'therapy.manage');
  const [responses, setResponses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [busy, setBusy] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);
  const formRef = useRef(null);

  async function quickAdmit() {
    setBusy(true);
    try {
      if (DEMO) {
        const demoEnr = { id: 'enr-' + Date.now(), program_type: programType || 'php', admitted_at: new Date().toISOString().slice(0, 10), status: 'active' };
        setEnrollments(prev => [demoEnr, ...prev]);
        setForm(f => ({ ...f, enrollment_id: demoEnr.id }));
        notify('Admitted to program (demo).');
        return;
      }
      await api(`/patients/${patient.id}/php/admit`, {
        method: 'POST',
        body: {
          program_type: programType || 'php',
          admitted_on: new Date().toISOString().slice(0, 10),
          reason_for_admission: 'Admitted for comprehensive group psychotherapy.',
          clinical_rationale: 'Active structured outpatient treatment episode.',
        },
      });
      notify('Patient admitted to ' + (programType || 'php').toUpperCase() + '.');
      await load();
    } catch (err) {
      notify(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function quickCreateSession() {
    setBusy(true);
    try {
      const now = new Date();
      const end = new Date(now.getTime() + 90 * 60000);
      if (DEMO) {
        const newS = { id: Date.now(), name: `${(programType || 'PHP').toUpperCase()} Group Therapy`, session_start: now.toISOString(), session_end: end.toISOString(), program_type: programType || 'php' };
        setSessions(prev => [newS, ...prev]);
        setForm(f => ({ ...f, group_session_id: newS.id }));
        notify('Created group session (demo).');
        return;
      }
      const res = await api('/php/group-sessions', {
        method: 'POST',
        body: {
          name: `${(programType || 'PHP').toUpperCase()} Group Psychotherapy`,
          program_type: programType || 'php',
          topic: 'Cognitive Restructuring & Coping Skills',
          session_start: now.toISOString(),
          session_end: end.toISOString(),
          status: 'in_progress',
        },
      });
      notify('Created group psychotherapy session.');
      const s = await api('/php/group-sessions').catch(() => ({ sessions: [] }));
      const list = s.sessions || [];
      setSessions(list);
      if (res?.session_id) {
        setForm(f => ({ ...f, group_session_id: res.session_id }));
      } else if (list.length > 0) {
        setForm(f => ({ ...f, group_session_id: list[0].id }));
      }
    } catch (err) {
      notify(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function load() {
    try {
      if (DEMO) {
        setEnrollments([
          { id: 'enr-demo-1', program_type: 'php', admitted_at: '2026-10-01', status: 'active' },
        ]);
        setSessions([
          { id: 101, name: 'Morning CBT Group', session_start: new Date().toISOString(), program_type: 'php' },
          { id: 102, name: 'Afternoon Skills & DBT Group', session_start: new Date(Date.now() - 3600000 * 3).toISOString(), program_type: 'php' },
          { id: 103, name: 'Relapse Prevention Group', session_start: new Date(Date.now() - 86400000).toISOString(), program_type: 'php' },
        ]);
        setResponses([
          {
            id: 1,
            patient_id: patient.id,
            group_session_id: 101,
            group_name: 'Morning CBT Group',
            group_start: new Date().toISOString(),
            enrollment_id: 'enr-demo-1',
            version: 1,
            status: 'signed',
            attendance_status: 'present',
            participation_level: 'active',
            progress_rating: 'improving',
            presentation: 'Alert, appropriately dressed, fully oriented. Interacted cooperatively with facilitator and cohort.',
            assessment: 'Alert, appropriately dressed, fully oriented. Interacted cooperatively with facilitator and cohort.',
            intervention: 'Facilitated group cognitive restructuring exercises focusing on automatic thoughts and catastrophizing.',
            response_to_intervention: 'Actively engaged in 3-column thought dispute exercises and offered constructive peer support.',
            individualized_response: 'Identified automatic thoughts regarding returning to work; successfully reframed anxiety into proactive task prioritization.',
            response: 'Identified automatic thoughts regarding returning to work; successfully reframed anxiety into proactive task prioritization.',
            evaluation: 'Demonstrated solid grasp of cognitive reframing concepts and good emotional regulation throughout.',
            progress: 'Demonstrated solid grasp of cognitive reframing concepts and good emotional regulation throughout.',
            relevant_verbalization: 'I am recognizing that when I break down tasks into steps, my panic subsides.',
            attitude: 'Receptive and collaborative',
            concentration: 'Sustained throughout 90-minute session',
            follow_directions: 'Followed instructions without prompting',
            peer_interaction: 'Supportive, respectful, and appropriately engaged',
            clinical_barriers: 'Mild anticipatory anxiety regarding workload transition',
            si_status: 'none',
            hi_status: 'none',
            substance_concern: 'none',
            safety_concern: false,
            safety_note: '',
            signed_name: user?.name || 'Dr. Jane Smith',
            signer_title: user?.title || 'LCSW',
            signed_at: new Date(Date.now() - 86400000 * 2).toISOString(),
            created_at: new Date(Date.now() - 86400000 * 2).toISOString(),
          },
        ]);
        return;
      }

      const [r, e, s] = await Promise.all([
        api(`/patients/${patient.id}/php/group-responses`).catch(() => ({ notes: [] })),
        api(`/patients/${patient.id}/php/enrollments`).catch(() => ({ enrollments: [] })),
        api('/php/group-sessions').catch(() => ({ sessions: [] })),
      ]);
      const loadedResponses = r.notes || r.responses || [];
      const loadedEnrollments = e.enrollments || [];
      let loadedSessions = s.sessions || [];

      // Auto-provision a default group session if none exist in the facility
      if (loadedSessions.length === 0 && !DEMO) {
        try {
          const now = new Date();
          const end = new Date(now.getTime() + 90 * 60000);
          await api('/php/group-sessions', {
            method: 'POST',
            body: {
              name: `${(programType || 'PHP').toUpperCase()} Group Psychotherapy`,
              program_type: programType || 'php',
              topic: 'Cognitive Restructuring & Coping Skills',
              session_start: now.toISOString(),
              session_end: end.toISOString(),
              status: 'in_progress',
            },
          });
          const refreshed = await api('/php/group-sessions').catch(() => ({ sessions: [] }));
          loadedSessions = refreshed.sessions || [];
        } catch (_) {}
      }

      setResponses(loadedResponses);
      setEnrollments(loadedEnrollments);
      setSessions(loadedSessions);

      // Prepopulate form defaults if empty
      setForm(f => {
        const activeEnr = loadedEnrollments.find(x => x.status === 'active') || loadedEnrollments[0];
        return {
          ...f,
          enrollment_id: f.enrollment_id || (activeEnr ? activeEnr.id : ''),
          group_session_id: f.group_session_id || (loadedSessions[0]?.id || ''),
        };
      });
    } catch (err) {
      notify(err.message);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [patient.id]);

  const set = k => e => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(f => ({ ...f, [k]: val }));
  };

  function openCreate() {
    const activeEnr = enrollments.find(e => e.status === 'active') || enrollments[0];
    const latestSession = sessions[0];
    setForm(emptyForm(activeEnr?.id || '', latestSession?.id || ''));
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }

  function edit(r) {
    setForm({
      id: r.id,
      addendum_of: null,
      original_version: r.version || 1,
      group_session_id: r.group_session_id || '',
      enrollment_id: r.enrollment_id || '',
      attendance_status: r.attendance_status || 'present',
      participation_level: r.participation_level || '',
      progress_rating: r.progress_rating || '',
      status: r.status === 'signed' || r.status === 'voided' ? r.status : 'draft',
      assessment: r.assessment || r.presentation || '',
      presentation: r.presentation || r.assessment || '',
      intervention: r.intervention || r.response_to_intervention || '',
      response_to_intervention: r.response_to_intervention || r.intervention || '',
      individualized_response: r.individualized_response || r.response || '',
      response: r.response || r.individualized_response || '',
      evaluation: r.evaluation || r.progress || '',
      progress: r.progress || r.evaluation || '',
      relevant_verbalization: r.relevant_verbalization || '',
      attitude: r.attitude || '',
      concentration: r.concentration || '',
      follow_directions: r.follow_directions || '',
      peer_interaction: r.peer_interaction || '',
      clinical_barriers: r.clinical_barriers || '',
      continued_need: r.continued_need || '',
      si_status: r.si_status || '',
      hi_status: r.hi_status || '',
      substance_concern: r.substance_concern || '',
      safety_concern: !!r.safety_concern,
      safety_note: r.safety_note || '',
      plan: r.plan || '',
    });
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }

  function openAddendum(r) {
    setForm({
      id: null,
      addendum_of: r.id,
      original_version: r.version || 1,
      group_session_id: r.group_session_id || '',
      enrollment_id: r.enrollment_id || '',
      attendance_status: r.attendance_status || 'present',
      participation_level: r.participation_level || '',
      progress_rating: r.progress_rating || '',
      status: 'draft',
      assessment: r.assessment || r.presentation || '',
      presentation: r.presentation || r.assessment || '',
      intervention: r.intervention || r.response_to_intervention || '',
      response_to_intervention: r.response_to_intervention || r.intervention || '',
      individualized_response: r.individualized_response || r.response || '',
      response: r.response || r.individualized_response || '',
      evaluation: r.evaluation || r.progress || '',
      progress: r.progress || r.evaluation || '',
      relevant_verbalization: r.relevant_verbalization || '',
      attitude: r.attitude || '',
      concentration: r.concentration || '',
      follow_directions: r.follow_directions || '',
      peer_interaction: r.peer_interaction || '',
      clinical_barriers: r.clinical_barriers || '',
      continued_need: r.continued_need || '',
      si_status: r.si_status || '',
      hi_status: r.hi_status || '',
      substance_concern: r.substance_concern || '',
      safety_concern: !!r.safety_concern,
      safety_note: r.safety_note || '',
      plan: r.plan || '',
    });
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }

  async function handleSign(responseId) {
    setBusy(true);
    try {
      if (DEMO) {
        setResponses(prev => prev.map(r => r.id === responseId ? {
          ...r,
          status: 'signed',
          signed_at: new Date().toISOString(),
          signed_name: user?.name || 'Physician',
          signer_title: user?.title || 'MD',
        } : r));
        notify('Group response signed (demo).');
        return;
      }
      await api(`/patients/${patient.id}/php/group-responses/${responseId}/sign`, { method: 'POST', body: {} });
      notify('Group response signed.');
      await load();
    } catch (err) {
      notify(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function submit(e, signImmediately = false) {
    if (e) e.preventDefault();

    if (!form.group_session_id) {
      notify('Please select a group session.');
      return;
    }
    if (!form.enrollment_id) {
      notify('Please select an enrollment episode.');
      return;
    }
    const narrative = form.individualized_response || form.response;
    if (!narrative.trim()) {
      notify('An individualized response narrative is required.');
      return;
    }

    setBusy(true);
    try {
      const selectedSession = sessions.find(s => String(s.id) === String(form.group_session_id));
      const groupName = selectedSession?.name || 'Group session';
      const groupStart = selectedSession?.session_start || new Date().toISOString();
      const desiredStatus = signImmediately ? 'signed' : form.status;

      const payload = {
        ...form,
        presentation: form.presentation || form.assessment,
        assessment: form.assessment || form.presentation,
        response_to_intervention: form.response_to_intervention || form.intervention,
        intervention: form.intervention || form.response_to_intervention,
        individualized_response: narrative,
        response: narrative,
        progress: form.progress || form.evaluation,
        evaluation: form.evaluation || form.progress,
        status: desiredStatus,
      };

      if (form.addendum_of) {
        if (DEMO) {
          const newAddendum = {
            ...payload,
            id: Date.now(),
            patient_id: patient.id,
            group_name: groupName,
            group_start: groupStart,
            addendum_of: form.addendum_of,
            version: (form.original_version || 1) + 1,
            created_at: new Date().toISOString(),
            signed_at: signImmediately ? new Date().toISOString() : null,
            signed_name: signImmediately ? (user?.name || 'Physician') : '',
            signer_title: signImmediately ? (user?.title || 'MD') : '',
          };
          setResponses(prev => [newAddendum, ...prev]);
          notify('Addendum recorded as a new version (demo).');
          setForm(emptyForm());
          return;
        }

        const res = await api(`/patients/${patient.id}/php/group-responses/${form.addendum_of}/addendum`, {
          method: 'POST',
          body: payload,
        });
        if (signImmediately && res?.response_id) {
          await api(`/patients/${patient.id}/php/group-responses/${res.response_id}/sign`, { method: 'POST', body: {} });
        }
        notify('Addendum recorded as a new version.');
        setForm(emptyForm());
        await load();
        return;
      }

      // Create or Edit mode
      if (DEMO) {
        if (form.id) {
          setResponses(prev => prev.map(r => r.id === form.id ? {
            ...r,
            ...payload,
            signed_at: desiredStatus === 'signed' ? new Date().toISOString() : r.signed_at,
            signed_name: desiredStatus === 'signed' ? (user?.name || 'Physician') : r.signed_name,
          } : r));
          notify('Group therapy note updated (demo).');
        } else {
          const newResp = {
            ...payload,
            id: Date.now(),
            patient_id: patient.id,
            group_name: groupName,
            group_start: groupStart,
            version: 1,
            provider_name: user?.name || 'Physician',
            signed_at: desiredStatus === 'signed' ? new Date().toISOString() : null,
            signed_name: desiredStatus === 'signed' ? (user?.name || 'Physician') : '',
            created_at: new Date().toISOString(),
          };
          setResponses(prev => [newResp, ...prev]);
          notify('Group therapy note recorded (demo).');
        }
        setForm(emptyForm());
        return;
      }

      const res = await api(`/patients/${patient.id}/php/group-responses`, {
        method: 'POST',
        body: payload,
      });

      if (signImmediately && desiredStatus !== 'signed') {
        const respId = res?.response_id || form.id;
        if (respId) {
          await api(`/patients/${patient.id}/php/group-responses/${respId}/sign`, { method: 'POST', body: {} });
        }
      }

      notify(form.id ? 'Group therapy note updated.' : 'Group therapy note recorded.');
      setForm(emptyForm());
      await load();
    } catch (err) {
      notify(err.message);
    } finally {
      setBusy(false);
    }
  }

  const originals = responses.filter(r => !r.addendum_of);
  const addenda = responses.filter(r => r.addendum_of);

  return (
    <div>
      <div className="content-heading">
        <div>
          <h4>Group therapy notes (A/I/R/E)</h4>
          <p>
            Assessment, intervention, individualized response, and evaluation, with a risk/safety review, group participation tracking, and treatment-plan linkage.
            Each participant's response is clinician-authored — never cloned from the shared group note.
            Signed notes are immutable; corrections are recorded as addenda.
          </p>
        </div>
        {canManage && (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button type="button" className="button ghost" onClick={() => setShowAiModal(true)}>
              <Sparkles size={14} /> <span>AI note generator</span>
            </button>
            <button type="button" className="button primary" onClick={openCreate}>
              <Plus size={14} /> <span>New group note</span>
            </button>
          </div>
        )}
      </div>

      {responses.length === 0 && <p className="empty-copy">No group therapy notes yet.</p>}

      {originals.map(r => {
        const relatedAddenda = addenda.filter(a => a.addendum_of === r.id);
        const isSigned = r.status === 'signed';
        const isDraftOrReady = r.status === 'draft' || r.status === 'ready_for_signature';

        return (
          <div key={r.id} className="php-plan">
            <div className="php-plan-head">
              <span className={`badge ${GROUP_NOTE_STATUS_BADGE[r.status] || 'badge-neutral'}`}>
                {GROUP_NOTE_STATUS_LABELS[r.status] || r.status}
              </span>
              {r.attendance_status && (
                <span className={`badge ${GROUP_ATTENDANCE_BADGE[r.attendance_status] || 'badge-neutral'}`}>
                  {GROUP_ATTENDANCE_LABELS[r.attendance_status] || r.attendance_status}
                </span>
              )}
              <span className="badge badge-neutral">v{r.version || 1}</span>
              <strong>{r.group_name || 'Group session'}</strong>
              {r.group_start && <small>{new Date(r.group_start).toLocaleString()}</small>}
              {r.signed_at && (
                <small>
                  Signed {new Date(r.signed_at).toLocaleString()}
                  {r.signed_name ? ` by ${r.signed_name}` : ''}
                  {r.signer_title ? ` · ${r.signer_title}` : ''}
                </small>
              )}

              <div className="spacer" />

              {r.group_session_id && (
                <button
                  type="button"
                  className="text-button"
                  onClick={() => navigate && navigate(`/php-therapy/group/${r.group_session_id}`)}
                >
                  <span>Open group</span>
                </button>
              )}

              {canManage && isDraftOrReady && (
                <>
                  <button type="button" className="button ghost" onClick={() => edit(r)}>
                    <span>Edit</span>
                  </button>
                  <button
                    type="button"
                    className="button ghost"
                    disabled={busy}
                    onClick={() => handleSign(r.id)}
                  >
                    <span>Sign</span>
                  </button>
                </>
              )}

              {canManage && isSigned && (
                <button
                  type="button"
                  className="button ghost"
                  disabled={busy}
                  onClick={() => openAddendum(r)}
                >
                  <PenLine size={14} /> <span>Addendum</span>
                </button>
              )}
            </div>

            <div className="php-plan-body" style={{ padding: '4px 6px' }}>
              {(r.assessment || r.presentation) && (
                <p>
                  <strong>Assessment (A).</strong> {r.assessment || r.presentation}
                </p>
              )}

              {(r.intervention || r.response_to_intervention) && (
                <p>
                  <strong>Intervention (I).</strong> {r.intervention || r.response_to_intervention}
                </p>
              )}

              {(r.individualized_response || r.response) && (
                <p>
                  <strong>Individualized response (R).</strong> {r.individualized_response || r.response}
                </p>
              )}

              {(r.evaluation || r.progress) && (
                <p>
                  <strong>Evaluation (E).</strong> {r.evaluation || r.progress}
                </p>
              )}

              {(r.participation_level || r.progress_rating || r.attendance_status) && (
                <p className="muted" style={{ margin: '4px 0' }}>
                  <small>
                    {[
                      r.attendance_status && `Attendance: ${GROUP_ATTENDANCE_LABELS[r.attendance_status] || r.attendance_status}`,
                      r.participation_level && `Participation: ${r.participation_level}`,
                      r.progress_rating && `Progress rating: ${r.progress_rating}`,
                    ].filter(Boolean).join(' · ')}
                  </small>
                </p>
              )}

              {r.relevant_verbalization && (
                <p className="muted" style={{ margin: '4px 0' }}>
                  <small><strong>Relevant verbalization:</strong> {r.relevant_verbalization}</small>
                </p>
              )}

              {[
                r.attitude && `Attitude: ${r.attitude}`,
                r.concentration && `Concentration: ${r.concentration}`,
                r.follow_directions && `Follows directions: ${r.follow_directions}`,
                r.peer_interaction && `Peer interaction: ${r.peer_interaction}`,
              ].filter(Boolean).length > 0 && (
                <p className="muted" style={{ margin: '4px 0' }}>
                  <small>
                    {[
                      r.attitude && `Attitude: ${r.attitude}`,
                      r.concentration && `Concentration: ${r.concentration}`,
                      r.follow_directions && `Follows directions: ${r.follow_directions}`,
                      r.peer_interaction && `Peer interaction: ${r.peer_interaction}`,
                    ].filter(Boolean).join(' · ')}
                  </small>
                </p>
              )}

              {(r.continued_need || r.clinical_barriers) && (
                <div style={{ margin: '4px 0' }}>
                  {r.continued_need && <p className="muted" style={{ margin: '2px 0' }}><small><strong>Continued need:</strong> {r.continued_need}</small></p>}
                  {r.clinical_barriers && <p className="muted" style={{ margin: '2px 0' }}><small><strong>Clinical barriers:</strong> {r.clinical_barriers}</small></p>}
                </div>
              )}

              {(r.si_status || r.hi_status || r.substance_concern) && (
                <p className="muted" style={{ margin: '4px 0' }}>
                  <small>Risk review — SI: {r.si_status || '—'} · HI: {r.hi_status || '—'} · Substance: {r.substance_concern || '—'}</small>
                </p>
              )}

              {r.safety_concern && (
                <p style={{ color: '#dc2626', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px', margin: '6px 0' }}>
                  <ShieldAlert size={14} /> <span>Safety concern documented{r.safety_note ? ` — ${r.safety_note}` : ''}</span>
                </p>
              )}

              {r.plan && (
                <p>
                  <strong>Plan.</strong> {r.plan}
                </p>
              )}
            </div>

            {relatedAddenda.map(a => (
              <div key={a.id} className="therapy-addendum">
                <div className="meta" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
                  <span><strong>Addendum v{a.version}</strong> — {a.created_at ? new Date(a.created_at).toLocaleString() : 'Recent'}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span className={`badge ${GROUP_NOTE_STATUS_BADGE[a.status] || 'badge-neutral'}`}>
                      {GROUP_NOTE_STATUS_LABELS[a.status] || a.status}
                    </span>
                    {canManage && (a.status === 'draft' || a.status === 'ready_for_signature') && (
                      <>
                        <button type="button" className="button ghost small" onClick={() => edit(a)}>
                          <span>Edit</span>
                        </button>
                        <button type="button" className="button ghost small" disabled={busy} onClick={() => handleSign(a.id)}>
                          <span>Sign</span>
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {(a.assessment || a.presentation) && <p><strong>Assessment (A).</strong> {a.assessment || a.presentation}</p>}
                {(a.intervention || a.response_to_intervention) && <p><strong>Intervention (I).</strong> {a.intervention || a.response_to_intervention}</p>}
                {(a.individualized_response || a.response) && <p><strong>Individualized response (R).</strong> {a.individualized_response || a.response}</p>}
                {(a.evaluation || a.progress) && <p><strong>Evaluation (E).</strong> {a.evaluation || a.progress}</p>}
                {a.safety_concern && (
                  <p style={{ color: '#dc2626', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 600 }}>
                    <ShieldAlert size={14} /> <span>Safety concern: {a.safety_note || 'Documented'}</span>
                  </p>
                )}
                {a.plan && <p className="muted"><small><strong>Plan:</strong> {a.plan}</small></p>}
                {a.signed_at && (
                  <p className="muted" style={{ margin: '4px 0 0' }}>
                    <small>
                      Signed {new Date(a.signed_at).toLocaleString()}
                      {a.signed_name ? ` by ${a.signed_name}` : ''}
                      {a.signer_title ? ` · ${a.signer_title}` : ''}
                    </small>
                  </p>
                )}
              </div>
            ))}
          </div>
        );
      })}

      {canManage && (
        <form ref={formRef} className="php-form section-space" onSubmit={e => submit(e, false)}>
          <div className="content-heading">
            <div>
              <h4>{form.addendum_of ? `Record addendum (v${(form.original_version || 1) + 1})` : form.id ? 'Edit group therapy note' : 'New group therapy note'}</h4>
              <p>
                {form.addendum_of
                  ? 'Signed notes are immutable — saving creates a new versioned addendum.'
                  : 'Individualized group psychotherapy documentation (A/I/R/E). Each note is clinician-authored.'}
              </p>
            </div>
            <button type="button" className="button ghost small" onClick={() => setShowAiModal(true)}>
              <Sparkles size={14} /> <span>AI Assist</span>
            </button>
          </div>

          <div className="form-grid">
            {sessions.length === 0 && (
              <div className="inline-alert" style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <span>No scheduled group sessions found for this facility.</span>
                <button type="button" className="button small primary" onClick={quickCreateSession} disabled={busy}>
                  <Plus size={13} /> <span>Create Group Session</span>
                </button>
              </div>
            )}
            {enrollments.length === 0 && (
              <div className="inline-alert" style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <span>No active program enrollment found for this patient.</span>
                <button type="button" className="button small primary" onClick={quickAdmit} disabled={busy}>
                  <Plus size={13} /> <span>Admit to {(programType || 'php').toUpperCase()}</span>
                </button>
              </div>
            )}
            <label>Group session *
              <select
                value={form.group_session_id}
                onChange={set('group_session_id')}
                disabled={!!form.addendum_of}
                required
              >
                <option value="">— Select group session —</option>
                {sessions.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.name} ({new Date(s.session_start).toLocaleDateString()}) · {s.program_type?.toUpperCase()}
                  </option>
                ))}
              </select>
            </label>

            <label>Enrollment episode *
              <select
                value={form.enrollment_id}
                onChange={set('enrollment_id')}
                disabled={!!form.addendum_of}
                required
              >
                <option value="">— Select enrollment episode —</option>
                {enrollments.map(en => (
                  <option key={en.id} value={en.id}>
                    {en.program_type?.toUpperCase()} · admitted {en.admitted_at} · {en.status}
                  </option>
                ))}
              </select>
            </label>

            <label>Attendance status
              <select value={form.attendance_status} onChange={set('attendance_status')}>
                {Object.entries(GROUP_ATTENDANCE_LABELS).map(([k, l]) => (
                  <option key={k} value={k}>{l}</option>
                ))}
              </select>
            </label>

            <label>Participation level
              <select value={form.participation_level} onChange={set('participation_level')}>
                <option value="">— Unset —</option>
                {PARTICIPATION_LEVELS.map(l => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </select>
            </label>

            <label>Progress rating
              <select value={form.progress_rating} onChange={set('progress_rating')}>
                <option value="">— Unset —</option>
                {PROGRESS_RATINGS.map(l => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </select>
            </label>

            <label>Note status
              <select value={form.status} onChange={set('status')}>
                <option value="draft">Draft</option>
                <option value="ready_for_signature">Ready to sign</option>
                <option value="signed">Signed</option>
              </select>
            </label>

            <label className="span2">Assessment (A)
              <textarea
                rows={3}
                value={form.assessment}
                onChange={set('assessment')}
                maxLength={8000}
                placeholder="Participant's presentation, mental status, and clinical topic check-in."
              />
            </label>

            <label className="span2">Intervention (I)
              <textarea
                rows={3}
                value={form.intervention}
                onChange={set('intervention')}
                maxLength={8000}
                placeholder="Specific therapeutic interventions, CBT exercises, skills training applied during group."
              />
            </label>

            <label className="span2">Individualized response (R) *
              <textarea
                rows={3}
                value={form.individualized_response}
                onChange={set('individualized_response')}
                maxLength={8000}
                placeholder="Clinician-authored narrative of participant's specific response to the group topic — never cloned from a shared note."
                required
              />
            </label>

            <label className="span2">Evaluation (E)
              <textarea
                rows={3}
                value={form.evaluation}
                onChange={set('evaluation')}
                maxLength={8000}
                placeholder="Evaluation of participant's response and progress toward treatment plan goals."
              />
            </label>

            <label className="span2">Relevant verbalization
              <textarea rows={2} value={form.relevant_verbalization} onChange={set('relevant_verbalization')} maxLength={8000} placeholder="Key statements or insights verbalized by the participant." />
            </label>

            <label>Attitude
              <input value={form.attitude} onChange={set('attitude')} maxLength={200} placeholder="e.g. receptive, guarded, cooperative" />
            </label>

            <label>Concentration
              <input value={form.concentration} onChange={set('concentration')} maxLength={200} placeholder="e.g. sustained, distractible" />
            </label>

            <label>Follow directions
              <input value={form.follow_directions} onChange={set('follow_directions')} maxLength={200} placeholder="e.g. followed without prompting" />
            </label>

            <label>Peer interaction
              <input value={form.peer_interaction} onChange={set('peer_interaction')} maxLength={200} placeholder="e.g. collaborative, supportive, reserved" />
            </label>

            <label className="span2">Continued need
              <textarea rows={2} value={form.continued_need} onChange={set('continued_need')} maxLength={8000} placeholder="Clinical justification for ongoing group psychotherapy." />
            </label>

            <label className="span2">Clinical barriers
              <textarea rows={2} value={form.clinical_barriers} onChange={set('clinical_barriers')} maxLength={8000} placeholder="Barriers to progress or participation." />
            </label>
          </div>

          <div className="content-heading section-space">
            <div>
              <h4>Risk / safety review</h4>
              <p>Documentation only — these fields never determine risk level, eligibility, or level of care.</p>
            </div>
          </div>
          <div className="form-grid">
            <label>Suicidal ideation (SI)
              <input value={form.si_status} onChange={set('si_status')} maxLength={30} placeholder="e.g. none / passive / active" />
            </label>
            <label>Homicidal ideation (HI)
              <input value={form.hi_status} onChange={set('hi_status')} maxLength={30} placeholder="e.g. none" />
            </label>
            <label>Substance concern
              <input value={form.substance_concern} onChange={set('substance_concern')} maxLength={30} placeholder="e.g. none / mild / moderate" />
            </label>
            <label className="checkbox-label">
              <input type="checkbox" checked={form.safety_concern} onChange={set('safety_concern')} />
              <span>Safety concern documented</span>
            </label>
            {form.safety_concern && (
              <label className="span2">Safety note
                <textarea rows={2} value={form.safety_note} onChange={set('safety_note')} maxLength={8000} placeholder="Detail clinical safety review and interventions taken." />
              </label>
            )}
            {hasCap(user, 'safety.manage') && form.safety_concern && (
              <div className="span2">
                <button type="button" className="button ghost" onClick={() => openAssessments && openAssessments()}>
                  <ShieldAlert size={14} /> <span>Start safety assessment</span>
                </button>
              </div>
            )}
            <label className="span2">Plan
              <textarea rows={2} value={form.plan} onChange={set('plan')} maxLength={8000} placeholder="Follow-up plan, homework, next group session." />
            </label>
          </div>

          <div className="php-plan-actions" style={{ marginTop: '16px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button type="submit" className="button primary" disabled={busy}>
              <span>{busy ? 'Saving…' : form.addendum_of ? 'Record addendum' : form.id ? 'Update note' : 'Save note'}</span>
            </button>
            {!form.addendum_of && form.status !== 'signed' && (
              <button
                type="button"
                className="button ghost"
                disabled={busy}
                onClick={() => submit(null, true)}
              >
                <span>{busy ? 'Saving…' : 'Save & Sign'}</span>
              </button>
            )}
            {(form.id || form.addendum_of) && (
              <button type="button" className="button ghost" onClick={() => setForm(emptyForm(enrollments[0]?.id || '', sessions[0]?.id || ''))} disabled={busy}>
                <span>Cancel edit</span>
              </button>
            )}
          </div>
        </form>
      )}

      {showAiModal && (
        <AiNoteGeneratorModal
          patient={patient}
          initialNoteType="group_therapy_note"
          programType={programType}
          onClose={() => setShowAiModal(false)}
          notify={notify}
          onApply={draft => {
            setForm(f => ({
              ...f,
              assessment: draft.assessment || draft.presentation || f.assessment,
              presentation: draft.presentation || draft.assessment || f.presentation,
              intervention: draft.intervention || draft.response_to_intervention || f.intervention,
              response_to_intervention: draft.response_to_intervention || draft.intervention || f.response_to_intervention,
              individualized_response: draft.individualized_response || draft.response || f.individualized_response,
              response: draft.response || draft.individualized_response || f.response,
              evaluation: draft.evaluation || draft.progress || f.evaluation,
              progress: draft.progress || draft.evaluation || f.progress,
              relevant_verbalization: draft.relevant_verbalization || f.relevant_verbalization,
              attitude: draft.attitude || f.attitude,
              concentration: draft.concentration || f.concentration,
              follow_directions: draft.follow_directions || f.follow_directions,
              peer_interaction: draft.peer_interaction || f.peer_interaction,
              clinical_barriers: draft.clinical_barriers || f.clinical_barriers,
              continued_need: draft.continued_need || f.continued_need,
              plan: draft.plan || f.plan,
              attendance_status: draft.attendance_status || f.attendance_status,
              participation_level: draft.participation_level || f.participation_level,
              progress_rating: draft.progress_rating || f.progress_rating,
              si_status: draft.si_status ?? f.si_status,
              hi_status: draft.hi_status ?? f.hi_status,
              substance_concern: draft.substance_concern ?? f.substance_concern,
              safety_concern: draft.safety_concern !== undefined ? draft.safety_concern : f.safety_concern,
              safety_note: draft.safety_note ?? f.safety_note,
            }));
          }}
        />
      )}
    </div>
  );
}

export { PhpGroupResponses as PhpGroupTherapyNotes };
